import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Candidate {
    id: string;
    tenantId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    roleApplied: string;
    status: string;
    resumeUrl?: string;
    source?: string;
    createdAt: string;
    updatedAt: string;
}

interface CandidateState {
    candidates: Candidate[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchCandidates: (filters?: any) => Promise<void>;
    createCandidate: (data: Partial<Candidate>) => Promise<Candidate>;
    updateCandidate: (id: string, data: Partial<Candidate>) => Promise<Candidate>;
    deleteCandidate: (id: string) => Promise<void>;
}

export const useCandidateStore = create<CandidateState>((set, get) => ({
    candidates: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchCandidates: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/candidates', { params: filters });
            const data = response.data?.data ?? response.data;
            const pagination = response.data?.pagination ?? { page: 1, limit: 20, total: Array.isArray(data) ? data.length : 0, totalPages: 1 };
            set({ candidates: Array.isArray(data) ? data : [], pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch candidates', isLoading: false });
        }
    },
    createCandidate: async (data: Partial<Candidate>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/candidates', data);
            const newCandidate = response.data?.data ?? response.data;
            set((state) => ({
                candidates: [newCandidate, ...state.candidates],
                isLoading: false,
            }));
            return newCandidate;
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create candidate', isLoading: false });
            throw error;
        }
    },
    updateCandidate: async (id: string, data: Partial<Candidate>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/candidates/${id}`, data);
            const updatedCandidate = response.data?.data ?? response.data;
            set((state) => ({
                candidates: state.candidates.map((o) => (o.id === id ? updatedCandidate : o)),
                isLoading: false,
            }));
            return updatedCandidate;
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update candidate', isLoading: false });
            throw error;
        }
    },
    deleteCandidate: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
            await apiClient.delete(`/people/candidates/${id}`);
            set((state) => ({
                candidates: state.candidates.filter((o) => o.id !== id),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete candidate', isLoading: false });
            throw error;
        }
    },
}));
